import produce from "immer"

export const actionTypes = {
  bankSelected: "bankSelected",

  uploadIncomeDocument: "uploadIncomeDocument",
  uploadIncomeDocumentInProgress: "uploadIncomeDocumentInProgress",
  uploadIncomeDocumentCompleted: "uploadIncomeDocumentCompleted",
  uploadIncomeDocumentFailed: "uploadIncomeDocumentFailed",
  resetUploadIncomeDocumentState: "resetUploadIncomeDocumentState",

  requestConsent: "requestConsent",
  requestConsentCompleted: "requestConsentCompleted",
  requestConsentFailed: "requestConsentFailed",
  pollConsentStatus: "pollConsentStatus",
  pollConsentStatusCompleted: "pollConsentStatusCompleted",
  pollConsentStatusFailed: "pollConsentStatusFailed",
  resetConsentState: "resetConsentState",

  consentedAccounts: "consentedAccounts",
  consentedAccountsCompleted: "consentedAccountsCompleted",
  consentedAccountsFailed: "consentedAccountsFailed",
  selectAccount: "selectAccount",
  selectAccountCompleted: "selectAccountCompleted",
  selectAccountFailed: "selectAccountFailed",
  resetAccountSelectionState: "resetAccountSelectionState",
  stopPollingConsentStatus: "stopPollingConsentStatus",
  onUserIdentifiedLandingBypassSelected: "onUserIdentifiedLandingBypassSelected",

  // File upload metrics
  filesValidated: "filesValidated",
  fileSizeExceeded: "fileSizeExceeded",
  mimeTypeValidationFailed: "mimeTypeValidationFailed",
  fileSignatureValidationFailed: "fileSignatureValidationFailed",
  duplicateFileDetected: "duplicateFileDetected",
  uploadMethodSelected: "uploadMethodSelected",
  fileRemovedAfterSelection: "fileRemovedAfterSelection"
}

export const actionFactory = {
  bankSelected: (bankName) => ({type: actionTypes.bankSelected, payload: {bankName}}),

  uploadIncomeDocument: payload => ({type: actionTypes.uploadIncomeDocument, payload}),
  uploadIncomeDocumentInProgress: payload => ({type: actionTypes.uploadIncomeDocumentInProgress, payload}),
  uploadIncomeDocumentCompleted: () => ({type: actionTypes.uploadIncomeDocumentCompleted}),
  uploadIncomeDocumentFailed: payload => ({type: actionTypes.uploadIncomeDocumentFailed, payload}),
  resetUploadIncomeDocumentState: () => ({type: actionTypes.resetUploadIncomeDocumentState}),

  requestConsent: payload => ({type: actionTypes.requestConsent, payload}),
  requestConsentCompleted: payload => ({type: actionTypes.requestConsentCompleted, payload}),
  requestConsentFailed: payload => ({type: actionTypes.requestConsentFailed, payload}),
  pollConsentStatus: payload => ({type: actionTypes.pollConsentStatus, payload}),
  pollConsentStatusCompleted: payload => ({type: actionTypes.pollConsentStatusCompleted, payload}),
  pollConsentStatusFailed: payload => ({type: actionTypes.pollConsentStatusFailed, payload}),
  resetConsentState: () => ({type: actionTypes.resetConsentState}),

  consentedAccounts: payload => ({type: actionTypes.consentedAccounts, payload}),
  consentedAccountsCompleted: payload => ({type: actionTypes.consentedAccountsCompleted, payload}),
  consentedAccountsFailed: payload => ({type: actionTypes.consentedAccountsFailed, payload}),
  selectAccount: payload => ({type: actionTypes.selectAccount, payload}),
  selectAccountCompleted: () => ({type: actionTypes.selectAccountCompleted}),
  selectAccountFailed: payload => ({type: actionTypes.selectAccountFailed, payload}),
  resetAccountSelectionState: () => ({type: actionTypes.resetAccountSelectionState}),
  stopPollingConsentStatus: () => ({type: actionTypes.stopPollingConsentStatus}),

  onUserIdentifiedLandingBypassSelected: () => ({type: actionTypes.onUserIdentifiedLandingBypassSelected}),

  // manual file upload metrics
  filesValidated: (payload) => ({type: actionTypes.filesValidated, payload}),
  fileSizeExceeded: (payload) => ({type: actionTypes.fileSizeExceeded, payload}),
  mimeTypeValidationFailed: (payload) => ({type: actionTypes.mimeTypeValidationFailed, payload}),
  fileSignatureValidationFailed: (payload) => ({type: actionTypes.fileSignatureValidationFailed, payload}),
  duplicateFileDetected: () => ({type: actionTypes.duplicateFileDetected}),
  uploadMethodSelected: (payload) => ({type: actionTypes.uploadMethodSelected, payload}),
  fileRemovedAfterSelection: () => ({type: actionTypes.fileRemovedAfterSelection})
}

export const incomeReducer = (state, action) => produce(state, draft => {
  const {type, payload} = action

  switch (type) {
    case actionTypes.bankSelected:
      draft.incomeState.selectedBankName = payload.bankName
      break

    case actionTypes.uploadIncomeDocument:
      draft.incomeState.uploadIncomeDocumentLoading = true
      draft.incomeState.uploadIncomeDocumentProgress = undefined
      draft.timingMap[actionTypes.uploadIncomeDocument] = new Date().getTime()
      break

    case actionTypes.uploadIncomeDocumentInProgress:
      draft.incomeState.uploadIncomeDocumentProgress = {
        completedFiles: payload.completedFiles,
        totalFiles: payload.totalFiles,
        currentFileName: payload.fileName
      }
      break

    case actionTypes.uploadIncomeDocumentCompleted:
      draft.incomeState.uploadIncomeDocumentLoading = false
      draft.incomeState.uploadIncomeDocumentCompleted = true
      draft.incomeState.uploadIncomeDocumentFailed = undefined
      draft.incomeState.uploadIncomeDocumentProgress = undefined
      draft.timingMap[actionTypes.uploadIncomeDocumentCompleted] = new Date().getTime()
      break

    case actionTypes.uploadIncomeDocumentFailed:
      draft.incomeState.uploadIncomeDocumentLoading = false
      draft.incomeState.uploadIncomeDocumentFailed = payload
      draft.incomeState.uploadIncomeDocumentCompleted = false
      draft.incomeState.uploadIncomeDocumentProgress = undefined
      break

    case actionTypes.resetUploadIncomeDocumentState:
      draft.incomeState.uploadIncomeDocumentLoading = false
      draft.incomeState.uploadIncomeDocumentCompleted = false
      draft.incomeState.uploadIncomeDocumentFailed = undefined
      draft.incomeState.uploadIncomeDocumentProgress = undefined
      break

    case actionTypes.requestConsent:
      draft.incomeState.requestConsentLoading = true
      draft.incomeState.requestConsentCompleted = false
      draft.incomeState.requestConsentFailed = undefined
      draft.incomeState.consentToken = payload.token
      break

    case actionTypes.requestConsentCompleted:
      draft.incomeState.requestConsentLoading = false
      draft.incomeState.requestConsentCompleted = true
      draft.incomeState.consentExpiryDateTime = payload.expiryDateTime
      break

    case actionTypes.requestConsentFailed:
      draft.incomeState.requestConsentLoading = false
      draft.incomeState.requestConsentFailed = payload
      draft.incomeState.requestConsentCompleted = false
      break

    case actionTypes.pollConsentStatus:
      draft.incomeState.pollConsentStatusLoading = true
      draft.incomeState.pollConsentStatusCompleted = false
      draft.incomeState.pollConsentStatusFailed = undefined
      break

    case actionTypes.pollConsentStatusCompleted:
      draft.incomeState.pollConsentStatusLoading = false
      draft.incomeState.pollConsentStatusCompleted = true
      draft.incomeState.consentStatus = payload.status
      break

    case actionTypes.pollConsentStatusFailed:
      draft.incomeState.pollConsentStatusLoading = false
      draft.incomeState.pollConsentStatusFailed = payload
      draft.incomeState.pollConsentStatusCompleted = false
      break

    case actionTypes.resetConsentState:
      draft.incomeState.requestConsentLoading = false
      draft.incomeState.requestConsentCompleted = false
      draft.incomeState.requestConsentFailed = undefined
      draft.incomeState.consentToken = undefined
      draft.incomeState.consentExpiryDateTime = undefined
      draft.incomeState.pollConsentStatusLoading = false
      draft.incomeState.pollConsentStatusCompleted = false
      draft.incomeState.pollConsentStatusFailed = undefined
      draft.incomeState.consentStatus = undefined
      break

    case actionTypes.consentedAccounts:
      draft.incomeState.consentedAccountsLoading = true
      draft.incomeState.consentedAccountsCompleted = false
      draft.incomeState.consentedAccountsFailed = undefined
      draft.incomeState.availableAccounts = undefined
      break

    case actionTypes.consentedAccountsCompleted:
      draft.incomeState.consentedAccountsLoading = false
      draft.incomeState.consentedAccountsCompleted = true
      draft.incomeState.availableAccounts = payload.accounts
      break

    case actionTypes.consentedAccountsFailed:
      draft.incomeState.consentedAccountsLoading = false
      draft.incomeState.consentedAccountsFailed = payload
      draft.incomeState.consentedAccountsCompleted = false
      break

    case actionTypes.selectAccount:
      draft.incomeState.selectAccountLoading = true
      draft.incomeState.selectAccountCompleted = false
      draft.incomeState.selectAccountFailed = undefined
      break

    case actionTypes.stopPollingConsentStatus:
      draft.incomeState.pollConsentStatusLoading = false
      break

    case actionTypes.selectAccountCompleted:
      draft.incomeState.selectAccountLoading = false
      draft.incomeState.selectAccountCompleted = true
      break

    case actionTypes.selectAccountFailed:
      draft.incomeState.selectAccountLoading = false
      draft.incomeState.selectAccountFailed = payload
      draft.incomeState.selectAccountCompleted = false
      break

    case actionTypes.resetAccountSelectionState:
      draft.incomeState.consentedAccountsLoading = false
      draft.incomeState.consentedAccountsCompleted = false
      draft.incomeState.consentedAccountsFailed = undefined
      draft.incomeState.availableAccounts = undefined
      draft.incomeState.selectAccountLoading = false
      draft.incomeState.selectAccountCompleted = false
      draft.incomeState.selectAccountFailed = undefined
      break

    case actionTypes.onUserIdentifiedLandingBypassSelected:
      draft.incomeState.userIdentifiedLandingBypassed = true
      break

    default:
  }
})